import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

/**
 * `/login/link?token=` — where the e-mailed sign-in link lands. The token is
 * spent by a POST from here, not by the GET, so a mail scanner that
 * prefetches the link doesn't burn it.
 */
export const Route = createFileRoute("/login_/link")({
  validateSearch: z.object({ token: z.string().optional() }),
  component: LoginLinkPage,
});

function LoginLinkPage() {
  const { token } = Route.useSearch();
  const [failed, setFailed] = useState(!token);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post<{ hasPassword?: boolean }>("/api/auth/email-link/redeem", { token })
      .then(({ hasPassword }) => {
        window.location.href = hasPassword === false ? "/profile" : "/";
      })
      .catch(() => setFailed(true));
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm space-y-4 text-center">
        {failed ? (
          <>
            <h1 className="font-heading text-xl font-bold">This link doesn't work any more</h1>
            <p className="text-sm text-muted-foreground">
              A sign-in link works once and only for a limited time. Ask for a new one.
            </p>
            <Button asChild>
              <Link to="/login">Back to sign-in</Link>
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
